import { TONE_INK, TONE_OKLCH } from '@frisk/shared';
import { t } from '../theme';
import type { ItemGroup } from '../store';

/**
 * One product, however many batches it has.
 *
 * The badge comes from the soonest batch — that is the one that needs acting
 * on. When the group is not uniformly urgent it also says how many are
 * actually affected: painting three jars red because one is old over-alarms,
 * and a colour you stop trusting is worse than no colour.
 */
export function ItemCard({ group, list, onTap }: { group: ItemGroup; list: boolean; onTap: () => void }) {
  const item = group.soonest;
  const colour = TONE_OKLCH[item.expiry.tone];
  const ink = TONE_INK[item.expiry.tone];
  const multi = group.batches.length > 1;
  const partly = multi && group.urgentQty > 0 && group.urgentQty < group.totalQty;

  /**
   * The label has no upper bound — "seit 128 Tagen" is a legal value — so the
   * badge cannot be trusted to fit a fixed-width thumbnail. In tile view it
   * sits on the image, which is wide enough; in list view the thumbnail is
   * only 118px and it moves into the text column instead of being clipped.
   */
  const badge = (
    <div
      style={{
        position: 'relative',
        alignSelf: 'flex-start',
        maxWidth: '100%',
        padding: '7px 14px',
        borderRadius: 999,
        background: colour,
        color: ink,
        fontSize: 18,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        boxShadow: !list && group.imageUrl ? '0 2px 10px -2px oklch(0 0 0 / 0.5)' : undefined,
      }}
    >
      {item.expiry.label}
    </div>
  );

  return (
    <div
      className="press"
      onClick={onTap}
      style={{
        borderRadius: 28,
        background: t.surfaceRaised,
        // A tile appearing among twelve is easy to miss entirely, which
        // matters when the scanning is happening on a phone across the room.
        boxShadow: group.justAdded
          ? `inset 0 0 0 2px ${t.accent}, 0 18px 34px -26px oklch(0.05 0 0 / 0.9)`
          : 'inset 0 1px 0 oklch(1 0 0 / 0.07), 0 18px 34px -26px oklch(0.05 0 0 / 0.9)',
        transition: 'box-shadow 400ms ease-out',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: list ? 'row' : 'column',
      }}
    >
      <div
        style={{
          flex: list ? 'none' : 1,
          width: list ? 118 : 'auto',
          minHeight: list ? 'auto' : 96,
          background: group.imageUrl
            ? `center / cover no-repeat url(${group.imageUrl})`
            : 'repeating-linear-gradient(135deg, oklch(0.3 0.012 60) 0 8px, oklch(0.265 0.012 60) 8px 16px)',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          padding: 12,
        }}
      >
        {/* Product photos are arbitrarily bright; a scrim keeps the badge legible. */}
        {!list && group.imageUrl && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, oklch(0 0 0 / 0.42) 0%, oklch(0 0 0 / 0) 46%)',
              pointerEvents: 'none',
            }}
          />
        )}
        {!list && badge}
      </div>

      <div
        style={{
          padding: list ? '18px 22px' : '16px 18px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          flex: 'none',
          minWidth: 0,
        }}
      >
        {list && badge}
        <div
          style={{
            fontSize: list ? 27 : 22,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            minHeight: list ? 'auto' : 53,
            textWrap: 'pretty',
            overflowWrap: 'anywhere',
          }}
        >
          {item.name}
        </div>
        <div style={{ fontFamily: t.mono, fontSize: 13, letterSpacing: '0.1em', color: 'oklch(0.64 0.015 70)' }}>
          {group.totalQty > 1 ? `${group.totalQty}× · ` : ''}
          {item.expiry.dateLabel}
          {/* Several dates behind one tile — say so rather than implying the
              whole group shares the badge's date. */}
          {multi && ` +${group.batches.length - 1}`}
        </div>
        {partly && (
          <div style={{ fontSize: 13, color: colour, fontWeight: 600, marginTop: -2 }}>
            {group.urgentQty} läuft ab
          </div>
        )}
        <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: 'oklch(1 0 0 / 0.08)', overflow: 'hidden' }}>
          <div style={{ width: `${item.expiry.percentLeft}%`, height: '100%', borderRadius: 2, background: colour }} />
        </div>
      </div>
    </div>
  );
}
